import { ReactElement, useEffect, useRef, useState, ChangeEvent, KeyboardEvent } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Container, Navbar } from 'react-bootstrap';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { Box, TextField } from '@mui/material';
import Logo from '../../assets/img/logo_nav.png';
import MobileLogo from '../../assets/img/logo_region_mobilenav.png';
import Basket from '../Basket/BasketDropdown';
import { WEBSHOP_ENABLED } from '../../constants/Settings';
import LineAwesome from '../LineAwesome/LineAwesome';
import { SideBookMarks } from '../SideBookMarks/SideBookMarks';
import { CustomNavigation } from './CustomNavigation';
import { NavIcons } from '../newNav/NavIcons';
import { regionStore } from '../../store/jotai/store';
import { userAtom } from '../../store/jotai/atoms/user.atom';
import { SearchTypeSelect } from '../SearchTypeSelect/SearchTypeSelect';
import { getSearchPath, SearchType, SearchTypeValue } from '../../constants/SearchType';
import './Header.scss';

export const Header = (): ReactElement => {
  const { t } = useTranslation();
  const history = useHistory();
  const user = useAtomValue(userAtom, { store: regionStore });

  const nodeRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const [isNavExpanded, setIsNavExpanded] = useState(false);
  const [openCard, setOpenCard] = useState(false);
  const [openBookmarks, setOpenBookmarks] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [showMobile, setShowMobile] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState<SearchTypeValue>(SearchType.All);

  // Close the expanded nav when a click lands outside the header.
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (nodeRef.current && !nodeRef.current.contains(event.target as Node)) {
        setIsNavExpanded(false);
      }
    };
    document.addEventListener('click', handleOutsideClick, false);
    return () => document.removeEventListener('click', handleOutsideClick, false);
  }, []);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  // Every search cancels the pending debounced one, so Enter or a type change
  // right after typing navigates once, not twice.
  const runSearch = (term: string, type: SearchTypeValue) => {
    clearTimeout(debounceRef.current);
    setSearchOpen(false);
    history.push(getSearchPath(term, type));
  };

  // Debounced search: fire 1s after the last keystroke. The timer keeps the
  // type it was scheduled with, which stays current because a type change
  // cancels it.
  const changeSearchTerm = (term: string) => {
    setSearchTerm(term);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(term, searchType), 1000);
  };

  // A new type re-runs a search that already has a term, like typing does.
  const changeSearchType = (type: SearchTypeValue) => {
    setSearchType(type);
    clearTimeout(debounceRef.current);
    if (searchTerm) {
      runSearch(searchTerm, type);
    }
  };

  // Lock body scroll while a sidebar (basket or bookmarks) is open, so only the
  // sidebar's own content scrolls. They are mutually exclusive, so owning the
  // lock here (rather than per-panel) avoids order-dependent add/remove races.
  useEffect(() => {
    document.body.classList.toggle('no-scroll', openCard || openBookmarks);
    return () => document.body.classList.remove('no-scroll');
  }, [openCard, openBookmarks]);

  const closeMenu = () => setIsNavExpanded(false);
  const onLinkClick = () => setShowMobile(false);
  const toggleSearch = () => setSearchOpen((open) => !open);

  const toggleCart = () => {
    setOpenCard((open) => !open);
    setOpenBookmarks(false);
    setShowMobile(false);
  };

  const toggleBookmarks = () => {
    setOpenBookmarks((open) => !open);
    setOpenCard(false);
    setShowMobile(false);
  };

  return (
    <div ref={nodeRef}>
      <div className='header-spacer' />
      <Navbar
        onToggle={setIsNavExpanded}
        expanded={isNavExpanded}
        color='light'
        expand='lg'
        className='navbar-light osahan-nav'>
        <Container className='navigation-container'>
          <Navbar.Brand>
            <Link to='/'>
              <img
                src={Logo}
                className='header__logo'
                alt='Region im Netz'
                onClick={() => setShowMobile(false)}
              />
            </Link>
          </Navbar.Brand>
          <div className='header__icons'>
            <NavIcons
              onLinkClick={onLinkClick}
              toggleBookmarks={toggleBookmarks}
              toggleCart={toggleCart}
            />
            <Navbar.Toggle onClick={() => setShowMobile((mobile) => !mobile)} />
          </div>

          <AnimatePresence exitBeforeEnter>
            {showMobile ? (
              <motion.div
                key={1}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className='mobile-nav'
                id='mobile-nav'>
                <div className='header__mobile-top'>
                  <Navbar.Brand>
                    <Link to='/'>
                      <img
                        src={MobileLogo}
                        className='header__logo'
                        alt='Region im Netz'
                        onClick={() => setShowMobile(false)}
                      />
                    </Link>
                  </Navbar.Brand>
                  <div className='header__mobile-close'>
                    <LineAwesome
                      icon='times'
                      onClick={() => setShowMobile(false)}
                      className='close-nav-mobile'
                    />
                  </div>
                </div>
                <CustomNavigation
                  handleSelect={closeMenu}
                  onLinkClick={onLinkClick}
                  isMobile
                  toggleBookmarks={toggleBookmarks}
                  toggleCart={toggleCart}
                  searchToggle={toggleSearch}
                />
              </motion.div>
            ) : null}
          </AnimatePresence>
          <AnimatePresence exitBeforeEnter>
            {searchOpen && (
              <motion.div
                key={1}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className='navbar-collapse collapse'
                id='navbarNavDropdown'>
                <Box className='header__search-nav'>
                  <Box className='header__search'>
                    <TextField
                      id='searchInput'
                      className='header__search-input'
                      type='text'
                      size='small'
                      label={t('navbar.search', 'Suche')}
                      value={searchTerm}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        changeSearchTerm(e.target.value)
                      }
                      onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === 'Enter') {
                          runSearch(searchTerm, searchType);
                        }
                      }}
                    />
                    <SearchTypeSelect value={searchType} onChange={changeSearchType} />
                    <LineAwesome
                      icon='times'
                      className='search-close'
                      onClick={() => setSearchOpen(false)}
                    />
                  </Box>
                </Box>
              </motion.div>
            )}

            {!searchOpen && (
              <motion.div
                key={2}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className='navbar-collapse collapse'
                id='navbarNavDropdown'>
                <CustomNavigation
                  handleSelect={closeMenu}
                  toggleBookmarks={toggleBookmarks}
                  toggleCart={toggleCart}
                  searchToggle={toggleSearch}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </Container>
      </Navbar>

      {WEBSHOP_ENABLED && <Basket openCard={openCard} toggleCart={toggleCart} />}

      {user?.id && (
        <SideBookMarks openBookmarks={openBookmarks} toggleBookmarks={toggleBookmarks} />
      )}
    </div>
  );
};
